// frontend/src/pages/ImportPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ImportPage.css';

function ImportPage() {
  const [file, setFile] = useState(null);
  const [tables, setTables] = useState([]);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [aide, setAide] = useState(null);
  const [importMode, setImportMode] = useState('replace');

  useEffect(() => {
    fetch('/api/tables')
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Erreur API: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success && data.tables) {
          setTables(data.tables.map((t) => t.name));
        } else {
          throw new Error('Format de réponse inattendu');
        }
      })
      .catch((error) => {
        console.error('Erreur chargement tables:', error);
      });
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) {
      setError("Veuillez d'abord sélectionner un fichier");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (file.name.endsWith('.zip')) {
        setPreview({
          type: 'zip',
          message: 'Fichier ZIP détecté - contiendra plusieurs tables',
        });
      } else {
        const content = await file.text();

        if (file.name.endsWith('.json')) {
          try {
            // Vérifier que le contenu n'est pas vide et semble être du JSON
            const trimmedContent = content.trim();
            if (!trimmedContent) {
              throw new Error('Le fichier est vide');
            }

            // Vérifier que le contenu commence par { ou [
            if (!trimmedContent.startsWith('{') && !trimmedContent.startsWith('[')) {
              throw new Error('Le fichier ne contient pas de JSON valide (doit commencer par { ou [)');
            }

            const json = JSON.parse(content);
            const data = json.data || json;
            const tableName = json.metadata?.table || file.name.replace('.json', '');
            setPreview({
              type: 'json',
              data: data,
              tables: [tableName],
              recordCount: Array.isArray(data) ? data.length : Object.keys(data).length,
            });
          } catch (e) {
            setError('Fichier JSON invalide: ' + e.message);
          }
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n').filter((line) => line.trim() !== '');
          if (lines.length === 0) {
            setError('Fichier CSV vide');
            return;
          }
          const headers = lines[0].split(';');
          setPreview({
            type: 'csv',
            headers,
            sample: lines.slice(1, 6).map((line) => {
              const values = line.split(';');
              return headers.reduce((obj, header, i) => {
                obj[header] = values[i] || '';
                return obj;
              }, {});
            }),
          });
        }
      }
    } catch (err) {
      setError('Erreur de prévisualisation: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    const confirmMessage =
      importMode === 'replace'
        ? '⚠️ ATTENTION: Cette opération va ECRASER les données existantes. Continuer?'
        : 'Les données seront AJOUTEES aux données existantes. Les doublons seront ignorés. Continuer?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', importMode);

      const response = await fetch('/api/import', {
        method: 'POST',
        body: formData,
      });

      // Lire le body UNE SEULE FOIS comme texte
      const text = await response.text();

      // Essayer de parser en JSON
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        setError(`Erreur serveur: ${response.status} - ${response.statusText}\n${text.substring(0, 200)}`);
        return;
      }

      // Vérifier response.ok APRÈS avoir lu le body
      if (!response.ok) {
        setError(data.error || `Erreur ${response.status}: ${response.statusText}`);
        return;
      }

      if (data.success) {
        setResult(data);
        setAide(null);
      } else {
        setError(data.error || "Erreur lors de l'import");
        if (data.aide) setAide(data.aide);
      }
    } catch (err) {
      if (err.message.includes('Type de fichier non autorisé')) {
        setError('❌ Type de fichier non autorisé. Seuls les fichiers ZIP, CSV et JSON sont acceptés.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('❌ Impossible de joindre le serveur. Vérifiez votre connexion.');
      } else {
        setError('❌ Erreur réseau: ' + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="import-page">
      <h1>Import de la Base de Données</h1>

      <div className="import-section">
        <div className="file-upload">
          <input type="file" id="import-file" accept=".zip,.csv,.json" onChange={handleFileChange} disabled={isLoading} />
          <label htmlFor="import-file" className="file-label">
            {file ? file.name : 'Sélectionner un fichier (ZIP, CSV ou JSON)'}
          </label>
          {file && (
            <div className="file-info">
              <span>Taille: {(file.size / 1024 / 1024).toFixed(2)} MB</span>
              <span>Type: {file.type || 'inconnu'}</span>
            </div>
          )}
        </div>

        <div className="import-mode-section">
          <label className="import-mode-label">Mode d'import :</label>
          <div className="import-mode-options">
            <label className={`import-mode-option ${importMode === 'replace' ? 'active' : ''}`}>
              <input type="radio" value="replace" checked={importMode === 'replace'} onChange={(e) => setImportMode(e.target.value)} />
              <span className="mode-icon">🔄</span>
              <span className="mode-text">
                <strong>Remplacer</strong>
                <small>Ecrase les donnees existantes</small>
              </span>
            </label>
            <label className={`import-mode-option ${importMode === 'append' ? 'active' : ''}`}>
              <input type="radio" value="append" checked={importMode === 'append'} onChange={(e) => setImportMode(e.target.value)} />
              <span className="mode-icon">➕</span>
              <span className="mode-text">
                <strong>Ajouter</strong>
                <small>Conserve les donnees, ignore les doublons</small>
              </span>
            </label>
          </div>
        </div>

        <div className="actions">
          <button onClick={handlePreview} disabled={!file || isLoading} className="btn-preview">
            {isLoading ? 'Chargement...' : 'Previsualiser'}
          </button>

          <button onClick={handleImport} disabled={!file || isLoading} className={`btn-import ${importMode === 'append' ? 'btn-import-append' : ''}`}>
            {isLoading ? 'Import en cours...' : importMode === 'replace' ? 'Importer (remplacer)' : 'Importer (ajouter)'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">❌ {error}</div>}

      {aide && (
        <div className="aide-section">
          <details>
            <summary>💡 Aide - Ordre d'import et conseils</summary>
            <div
              className="aide-contenu"
              dangerouslySetInnerHTML={{
                __html: aide
                  .replace(/^### (.+)$/gm, '<h4>$1</h4>')
                  .replace(/^## (.+)$/gm, '<h3>$1</h3>')
                  .replace(/^# (.+)$/gm, '<h2>$1</h2>')
                  .replace(/^\| (.+) \|$/gm, (match) => {
                    const cells = match.split('|').filter((c) => c.trim());
                    return '<tr>' + cells.map((c) => `<td>${c.trim()}</td>`).join('') + '</tr>';
                  })
                  .replace(/^\|[-| ]+\|$/gm, '')
                  .replace(/(<tr>.*<\/tr>\n?)+/g, '<table class="aide-table">$&</table>')
                  .replace(/^- \*\*(.+?)\*\*\s*:\s*(.+)$/gm, '<li><strong>$1</strong> : $2</li>')
                  .replace(/^- (.+)$/gm, '<li>$1</li>')
                  .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n\n/g, '<br/>'),
              }}
            />
          </details>
        </div>
      )}

      {preview && (
        <div className="preview-section">
          <h3>Prévisualisation</h3>
          {preview.type === 'json' && (
            <div className="json-preview">
              <p>Tables détectées: {preview.tables.join(', ')}</p>
              <p>Nombre d'enregistrements: {preview.data.length}</p>
              <div className="sample-data">
                <h4>Échantillon (5 premiers)</h4>
                <pre>{JSON.stringify(preview.data.slice(0, 5), null, 2)}</pre>
              </div>
            </div>
          )}

          {preview.type === 'csv' && (
            <div className="csv-preview">
              <p>En-têtes: {preview.headers.join(', ')}</p>
              <p>Nombre de lignes: {preview.sample.length} (échantillon)</p>
              <table>
                <thead>
                  <tr>
                    {preview.headers.map((header, i) => (
                      <th key={i}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {preview.headers.map((header, colIndex) => (
                        <td key={colIndex}>{row[header]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.type === 'zip' && (
            <div className="zip-preview">
              <p>{preview.message}</p>
              <p>Le fichier sera décompressé et chaque table sera importée séparément.</p>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className={`result-section ${result.success ? 'success' : 'error'}`}>
          <h3>{result.success ? '✅ Import réussi' : '❌ Import échoué'}</h3>

          {result.success ? (
            <>
              <p>Tables importées: {result.results.length}</p>
              <ul>
                {result.results.map((r, i) => (
                  <li key={i}>
                    {r.table}: {r.count} enregistrement{r.count > 1 ? 's' : ''}
                    {importMode === 'replace'
                      ? ' importés'
                      : r.created !== undefined
                        ? ` (${r.created} créé${r.created > 1 ? 's' : ''}, ${r.updated} mis à jour)`
                        : ' ajoutés'}
                  </li>
                ))}
              </ul>
              <p>Total: {result.results.reduce((sum, r) => sum + r.count, 0)} enregistrements</p>
            </>
          ) : (
            <>
              <p>Erreurs rencontrées:</p>
              <ul>
                {result.errors.map((e, i) => (
                  <li key={i}>
                    {e.table ? `${e.table}: ` : ''}
                    {e.error}
                  </li>
                ))}
              </ul>
            </>
          )}

          <button
            onClick={() => {
              setFile(null);
              setPreview(null);
              setResult(null);
            }}
            className="btn-reset"
          >
            Nouveau fichier
          </button>
        </div>
      )}
    </div>
  );
}

export default ImportPage;
